import { createFileRoute } from "@tanstack/react-router";
import { MapPin, Clock, Smartphone, ShieldCheck, BarChart3, Users } from "lucide-react";
import { SolutionLanding } from "@/components/landing/SolutionLanding";

const URL = "https://busditeads.com/software-de-controle-de-ponto-para-construcao";
const TITLE = "Software de Controle de Ponto com GPS para Construção — Business Dite Ads";
const DESC =
  "Controle de ponto com GPS para obras: bater ponto pelo celular, geolocalização, folhas precisas e exportação para folha de pagamento. Teste 14 dias grátis.";

const faqs = [
  {
    q: "Como funciona o ponto com GPS?",
    a: "O trabalhador abre um link no celular e bate o ponto. A localização é capturada na entrada e na saída para confirmar que ele estava na obra — sem instalar app.",
  },
  {
    q: "Como evito ponto batido por outro?",
    a: "Cada trabalhador tem um token único, o GPS é registrado em cada batida e o sistema avisa quando a saída ocorre antes do tempo normal de turno.",
  },
  {
    q: "Exporta para folha de pagamento?",
    a: "Sim. As horas são resumidas por funcionário e período e alimentam direto a folha — com suporte a pagamento por hora e por diária.",
  },
  {
    q: "Funciona offline?",
    a: "A página do trabalhador roda em qualquer celular com navegador. As batidas sincronizam assim que o aparelho volta online.",
  },
];

export const Route = createFileRoute("/software-de-controle-de-ponto-para-construcao")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
    ],
    links: [
      { rel: "canonical", href: URL },
      {
        rel: "alternate",
        hrefLang: "en",
        href: "https://busditeads.com/construction-time-tracking-software",
      },
      {
        rel: "alternate",
        hrefLang: "es",
        href: "https://busditeads.com/software-de-control-horario-para-construccion",
      },
      { rel: "alternate", hrefLang: "pt", href: URL },
      {
        rel: "alternate",
        hrefLang: "x-default",
        href: "https://busditeads.com/construction-time-tracking-software",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: "pt-BR",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SolutionLanding
      eyebrow="Controle de Ponto para Construção"
      h1="Controle de ponto com GPS que sua equipe vai usar de verdade"
      subhead="Ponto pelo celular, GPS na obra, folhas precisas e exportação pronta para a folha de pagamento — feito para empreiteiros de alvenaria, telhado, concreto e gerais."
      primaryCtaLabel="Começar teste grátis de 14 dias"
      secondaryCtaLabel="Ver preços"
      benefits={[
        "Funciona em qualquer celular",
        "Sem instalar app",
        "GPS em cada batida",
        "Cancele quando quiser",
      ]}
      featuresHeading="Feito para a realidade do canteiro de obras"
      features={[
        {
          icon: Smartphone,
          title: "Ponto pelo celular",
          description:
            "A equipe abre um link seguro e bate o ponto. Sem instalações, sem app na loja, sem suporte de TI.",
        },
        {
          icon: MapPin,
          title: "Horas com GPS",
          description:
            "Capture a localização na entrada e saída. Veja na hora batidas fora da obra.",
        },
        {
          icon: Clock,
          title: "Folhas precisas",
          description:
            "Cálculo automático de turno, intervalo e confirmações para evitar batidas acidentais.",
        },
        {
          icon: BarChart3,
          title: "Custo de mão de obra por obra",
          description:
            "As horas se ligam aos projetos para você ver o custo real de mão de obra por obra, em tempo real.",
        },
        {
          icon: Users,
          title: "Equipes e funções",
          description:
            "Adicione funcionários e subempreiteiros, atribua funções e controle quem vê o quê.",
        },
        {
          icon: ShieldCheck,
          title: "Registros para auditoria",
          description:
            "Histórico imutável de batidas, log de edição e exportações para auditorias e disputas.",
        },
      ]}
      howItWorksHeading="Do ponto à folha em três passos"
      steps={[
        {
          title: "Envie o link para a equipe",
          description: "Gere um link seguro por trabalhador — envie por SMS, WhatsApp ou e-mail.",
        },
        {
          title: "A equipe bate ponto com GPS",
          description: "Cada batida tem hora e localização, ligada ao trabalhador e à obra.",
        },
        {
          title: "Aprove e pague",
          description:
            "Revise as folhas, corrija exceções e rode a folha com as horas já carregadas.",
        },
      ]}
      socialProofHeading="Por que empreiteiros escolhem nosso controle de ponto"
      socialProof={[
        "Reduza horas de preparo da folha para minutos",
        "Pare de pagar horas que não foram trabalhadas",
        "Saiba exatamente qual equipe está em qual obra",
        "Pegue saídas esquecidas com alertas inteligentes",
        "Suporta pagamento por hora e por diária",
        "Disponível em português, espanhol e inglês",
      ]}
      faqHeading="Perguntas frequentes"
      faqs={faqs}
      finalCtaHeading="Pare de correr atrás de folha de ponto. Acompanhe as horas reais."
      finalCtaSubhead="Teste o Business Dite Ads grátis por 14 dias — coloque toda a equipe batendo ponto no primeiro dia."
    />
  );
}
